import { AutoSaveStatus, Palette, useAutoSaveStatus, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Fragment, type ReactNode } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { conflictMessage } from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import {
  SelectOptionsUtils_CheckoutConfigFragment,
  SelectOptionsUtils_OptionGroupFragment,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { fail, idsOf } from "src/utils";
import { useSelectOptionsStep_SaveCheckoutConfigOptionsMutation } from "./1.SelectOptionsStep.generated";
import { AddOrModifyOptionsFormInput } from "./addOrModifyOptionsWorkflow";

type SaveOptionSelection = {
  /**
   * Saves the whole selection onto the Draft result config and answers with the server's reconciled result config. `toggledOptionIds`
   * are the options the user turned on or off this time; if any of them would drop other options, the save waits on the
   * "Option Conflict" confirmation first. Resolves to `undefined` when the user backs out or the save fails.
   */
  save: (
    optionIds: string[],
    toggledOptionIds: string[],
  ) => Promise<SelectOptionsUtils_CheckoutConfigFragment | undefined>;
  /** Whether a save is in flight, from the shared auto-save status so every caller locks its UI together. */
  saving: boolean;
};

/**
 * Saves a selection change, shared by the Select Options cards and the Right Side pane.
 *
 * Every change goes straight to the backend because only it knows how a selection cascades: it prunes options whose
 * prerequisites are no longer met or that now conflict, adds required options and personalization defaults (e.g.
 * turning "Add Washer" on selects a default washer), and re-prices the result. Whatever it answers with is what
 * `formState.configChange.optionIds` holds, so the cards and the pane are always working off the server's latest answer.
 */
export function useSaveOptionSelection(
  formState: ObjectState<AddOrModifyOptionsFormInput>,
  groups: SelectOptionsUtils_OptionGroupFragment[],
): SaveOptionSelection {
  const { projectId } = useRouteParams<ProjectParams>();
  const [saveOptions] = useSelectOptionsStep_SaveCheckoutConfigOptionsMutation();
  const { openModal } = useModal();
  const { status, triggerAutoSave, resolveAutoSave } = useAutoSaveStatus();

  function confirmConflict(message: ConflictMessage): Promise<boolean> {
    return new Promise((resolve) =>
      openModal({
        content: (
          <ConfirmationModal
            title="Option Conflict"
            icon={{ icon: "error", color: Palette.Yellow600 }}
            confirmationMessage={message.text}
            label={message.action === "Removing" ? "Remove" : "Select"}
            onConfirmAction={() => resolve(true)}
          />
        ),
        // Runs after a confirm too, by which point the promise is already settled
        onClose: () => resolve(false),
      }),
    );
  }

  async function save(optionIds: string[], toggledOptionIds: string[]) {
    const conflict = conflictMessageFor(groups, optionIds, toggledOptionIds);
    if (conflict && !(await confirmConflict(conflict))) return undefined;
    triggerAutoSave();
    try {
      const resultId = formState.configChange.resultId.value ?? fail("Change event has no result config");
      const { data } = await saveOptions({ variables: { input: { id: resultId, optionIds }, structureId: projectId } });
      const result = data?.saveCheckoutConfig.checkoutConfig ?? fail("Save returned no result config");
      formState.configChange.optionIds.set(idsOf(result.config.options, "readyPlanOption"));
      // Sets `needsReconcile` to disable the next steps until the user clicks Continue (or Save & Exit)
      formState.needsReconcile.set(true);
      resolveAutoSave();
      return result;
    } catch (e) {
      // If something went wrong on backend, the last reconciled selection stays the truth
      resolveAutoSave(e);
      return undefined;
    }
  }

  return { save, saving: status === AutoSaveStatus.SAVING };
}

type ConflictMessage = { text: ReactNode; action: "Selecting" | "Removing" };

/**
 * The "Option Conflict" message for the toggled options that would drop something, or `undefined` when none would.
 * A pick-one swap toggles both the old and the new selection, but the old one's `wouldDeselect` is empty (it was
 * selected), so there's no need to single out "the" selection. The action is "Selecting" when a conflicting option
 * is in the selection being saved, "Removing" otherwise.
 */
function conflictMessageFor(
  groups: SelectOptionsUtils_OptionGroupFragment[],
  optionIds: string[],
  toggledOptionIds: string[],
): ConflictMessage | undefined {
  const toggledIds = new Set(toggledOptionIds);
  const toggled = groups
    .flatMap((g) => g.options)
    .filter((o) => toggledIds.has(o.option.id) && o.wouldDeselect.nonEmpty);
  if (toggled.isEmpty) return undefined;
  const selected = new Set(optionIds);
  const action = toggled.some((o) => selected.has(o.option.id)) ? "Selecting" : "Removing";
  const text = toggled.map((o) => (
    <Fragment key={o.id}>{conflictMessage(o.option.name, o.wouldDeselect, action)}</Fragment>
  ));
  return { text, action };
}
